import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DownloadImageGallery } from './DownloadImageGallery';
import { DownloadDetailsView } from './DownloadDetailsView';
import { DownloadLinkRow } from './DownloadLinkRow';
import { ImageGalleryModal } from './ImageGalleryModal';
import { DownloadsManager } from '../../../managers/downloads-manager';
import { useManager } from '../../../../../core';
import { useAuth } from '../../../../../contexts/AuthContext';
import { isEntitled } from '../../../../../utils/membership';
import './DownloadDetailsLayout.css';

export function DownloadDetailsLayout({ download, onDownloadTracked }) {
  const { manager } = useManager(DownloadsManager);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [activeLinkIndex, setActiveLinkIndex] = useState(null);
  const [linkErrorState, setLinkErrorState] = useState(null);

  if (!download) return null;

  const normalizeAccessType = (accessType) => {
    const normalized = typeof accessType === 'string' ? accessType.trim().toLowerCase() : '';
    return normalized === 'member' ? 'members' : normalized;
  };

  // Defer to the shared isEntitled() helper (mirrors the backend rule). This
  // means past_due users within their grace window still count as having
  // active membership for download access — same as paid features elsewhere.
  const hasActiveMembership = (candidateUser) => isEntitled(candidateUser?.membership);

  const hasSupporterEntitlement = (candidateUser) => {
    const accessTags = Array.isArray(candidateUser?.productAccessTags)
      ? candidateUser.productAccessTags.map((tag) => String(tag).trim().toLowerCase())
      : [];

    return candidateUser?.isSupporter === true || accessTags.includes('supporter');
  };

  const getAccessNoticeForType = (requiredAccessType, { afterClick = false } = {}) => {
    const isAdmin = user?.role === 'admin';
    const isLoggedIn = Boolean(user);

    if (!requiredAccessType || requiredAccessType === 'anonymous') {
      return null;
    }

    if (requiredAccessType === 'free' && !isLoggedIn) {
      return {
        message: afterClick
          ? 'Please sign in to access this download.'
          : 'Sign in to unlock these download links.',
        actionLabel: 'Sign In / Sign Up',
        actionPath: '/auth',
      };
    }

    if (requiredAccessType === 'members' && !isAdmin && !hasActiveMembership(user)) {
      return {
        message: afterClick
          ? 'You need an active membership to download this file.'
          : 'These links require an active membership.',
        actionLabel: 'View Membership Options',
        actionPath: '/pricing',
      };
    }

    if (requiredAccessType === 'supporter' && !isAdmin && !hasSupporterEntitlement(user)) {
      return {
        message: afterClick
          ? 'This download is reserved for supporters.'
          : 'These links are reserved for supporters.',
        actionLabel: 'View Supporter Options',
        actionPath: '/pricing',
      };
    }

    if (requiredAccessType === 'admin' && !isAdmin) {
      return {
        message: afterClick
          ? 'This download is available to administrators only.'
          : 'These links are available to administrators only.',
        actionLabel: null,
        actionPath: null,
      };
    }

    return null;
  };

  const requiredAccessType = normalizeAccessType(download?.accessType);
  const defaultAccessNotice = getAccessNoticeForType(requiredAccessType);
  const isLinkLocked = Boolean(defaultAccessNotice);

  const handleDownload = async (link, linkIndex) => {
    if (!manager || !download?.id) {
      return;
    }

    setActiveLinkIndex(linkIndex);
    setLinkErrorState(null);

    try {
      const resolved = await manager.resolveDownloadLink(download.id, linkIndex);
      const didTrack = await manager.trackDownload(download.id);

      if (didTrack) {
        onDownloadTracked?.();
      }

      if (resolved?.downloadUrl) {
        window.open(resolved.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Failed to resolve download link:', error);
      const friendlyNotice =
        getAccessNoticeForType(error.requiredAccessType || requiredAccessType, {
          afterClick: true,
        }) || {};
      setLinkErrorState({
        message: friendlyNotice.message || 'You do not have access to this download.',
        requiredAccessType: error.requiredAccessType || requiredAccessType || null,
        actionLabel: friendlyNotice.actionLabel || error.actionLabel || null,
        actionPath: friendlyNotice.actionPath || error.actionPath || null,
      });
    } finally {
      setActiveLinkIndex(null);
    }
  };

  const getAccessNotice = () => {
    if (linkErrorState) {
      return linkErrorState;
    }
    return defaultAccessNotice;
  };

  const accessNotice = getAccessNotice();

  const handleImageClick = (imageIndex) => {
    setGalleryInitialIndex(imageIndex);
    setGalleryModalOpen(true);
  };

  const handleGalleryModalClose = () => {
    setGalleryModalOpen(false);
  };

  return (
    <div className="download-details-layout">
      <div className="download-details-main-row">
        <div className="download-details-images">
          <DownloadImageGallery
            download={download}
            onImageClick={handleImageClick}
          />
          {Array.isArray(download.links) && download.links.length > 0 && (
            <div className="download-details-links-section">
              {accessNotice && (
                <div className="download-details-links-error">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  <div className="download-details-links-error-content">
                    <span>{accessNotice.message}</span>
                    {accessNotice.actionLabel && accessNotice.actionPath && (
                      <button
                        type="button"
                        className="download-details-links-error-action"
                        onClick={() => navigate(accessNotice.actionPath)}
                      >
                        {accessNotice.actionLabel}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {download.links.map((link, index) => (
                <DownloadLinkRow
                  key={index}
                  link={link}
                  onDownload={() => handleDownload(link, index)}
                  isLoading={activeLinkIndex === index}
                  isLocked={isLinkLocked}
                />
              ))}
            </div>
          )}
        </div>
        <div className="download-details-separator"></div>
        <div className="download-details-info">
          <DownloadDetailsView download={download} />
        </div>
      </div>

      <ImageGalleryModal
        download={download}
        isOpen={galleryModalOpen}
        onClose={handleGalleryModalClose}
        initialIndex={galleryInitialIndex}
      />
    </div>
  );
}
